import React, { useEffect, useRef, useState } from 'react';
import { TextField, IconButton } from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import EditIcon from '@material-ui/icons/Edit';
import '../containers/Settings.css';

export default function EditableField({
    label,
    name,
    value,
    onChange,
    canEdit,
    multiline = false,
    rows = 1,
    type = 'text',
    fullWidth = true,
    style,
    resetKey,
    onBeginEdit,
}) {
    const inputRef = useRef(null);
    const userStartedRef = useRef(false);

    // indica si el usuario ha puesto este campo explícitamente en modo edición
    const [editing, setEditing] = useState(false);

    const isEmpty = !value || String(value).trim() === '';
    const isAccountField = name === 'accountNumber';

    // Función para enmascarar el número de cuenta (IBAN)
    const maskAccountNumber = (val) => {
        if (!val) return '';
        const clean = String(val).replace(/\s+/g, '');
        if (clean.length <= 8) return '****';

        const prefix = clean.slice(0, 4);  // p.ej. ES12
        const last4 = clean.slice(-4);     // últimos 4 dígitos

        return `${prefix} **** **** **** ${last4}`;
    };

    // Valor que se muestra en el input
    // - Si es accountNumber y NO está en edición -> masked SIEMPRE
    // - En cualquier otro caso -> valor real
    const displayValue =
        (isAccountField && !editing)
            ? maskAccountNumber(value)
            : (value || '');

    // Reglas de lectura/escritura:
    // - Si no puede editar -> siempre readOnly
    // - Si está vacío -> siempre editable (si canEdit)
    // - Si tiene valor -> editable sólo cuando editing = true
    const readOnly = !canEdit ? true : (!editing && !isEmpty);

    // Icono de editar:
    // - Sólo si puede editar
    // - Tiene valor
    // - Y no está en modo edición
    const showEditIcon = canEdit && !editing && !isEmpty;

    // Reset al guardar / descartar o cambiar permisos
    useEffect(() => {
        setEditing(false);
        userStartedRef.current = false;
        if (inputRef.current) {
            inputRef.current.blur();
        }
    }, [resetKey, canEdit]);

    const notifyBeginEdit = () => {
        if (!userStartedRef.current) {
            userStartedRef.current = true;
            onBeginEdit && onBeginEdit(true);
        }
    };

    const startEditing = () => {
        if (!canEdit) return;
        notifyBeginEdit();
        setEditing(true);

        setTimeout(() => {
            const el = inputRef.current;
            if (!el) return;
            el.focus();
            try {
                const len = el.value ? el.value.length : 0;
                if (typeof el.setSelectionRange === 'function') {
                    el.setSelectionRange(len, len);
                }
            } catch (e) {}
        }, 0);
    };

    const handleChange = (e) => {
        // Si el campo estaba vacío y el usuario empieza a escribir,
        // lo ponemos automáticamente en modo edición para que no se bloquee
        if (canEdit && isEmpty && !editing) {
            setEditing(true);
        }

        notifyBeginEdit();
        onChange && onChange(e);
    };

    // Botón copiar al portapapeles cuenta
    const handleCopy = async () => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(String(value));
            alert('Número de cuenta copiado al portapapeles');
        } catch (err) {
            console.error('Error copiando al portapapeles', err);
            alert('Error al copiar el número de cuenta');
        }
    };

    return (
        <div className="editable-wrapper" style={{ ...style }}>
            {showEditIcon && (
                <IconButton
                    className="edit-icon"
                    size="small"
                    onClick={startEditing}
                    title="Editar"
                >
                    <EditIcon color="primary" />
                </IconButton>
            )}

            <TextField
                inputRef={inputRef}
                label={label}
                name={name}
                value={displayValue}
                onChange={handleChange}
                variant="outlined"
                type={type}
                fullWidth={fullWidth}
                multiline={multiline}
                rows={rows}
                inputProps={
                    multiline
                        ? { style: { resize: 'vertical', overflow: 'auto' } }
                        : {}
                }
                InputProps={{
                    readOnly,
                    endAdornment: isAccountField && canEdit && !isEmpty && (
                        <IconButton
                            size="small"
                            onClick={handleCopy}
                            title="Copiar número de cuenta"
                        >
                            <FileCopyIcon fontSize="small" />
                        </IconButton>
                    ),
                }}
            />
        </div>
    );
}